// Shared pieces of the messaging pages (Notifications, Banners, Support; contracts/messaging.md): the
// localized content editor with one-click translation, the image upload, the button action, a switch and the
// player picker.

import { type Dispatch, type ReactNode, type SetStateAction, useId, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import {
  ACTION_LABEL,
  APP_LINK_LABEL,
  APP_LINKS,
  type AppLink,
  CONTENT_LIMITS,
  type Content,
  type ContentField,
  type ImageRef,
  LOCALE_LABEL,
  LOCALES,
  type Locale,
  type MediaMime,
  type MessageAction,
  NEW_IN_110,
  RTL_LOCALES,
  TRANSLATE_TARGETS,
  useTranslate,
  useUploadMedia,
} from "../../api/messaging";
import { usePlayers } from "../../api/queries";
import { useCan } from "../../auth/AuthContext";
import { Button, Chip, Field, InlineError, Select, TextInput, cx } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { shortId } from "../../lib/format";
import {
  aspectNote,
  blankDraft,
  draftFrom,
  draftFromContent,
  emptyDraft,
  englishKey,
  imageFileProblem,
  isHttpsUrl,
  type LocaleState,
  localeState,
  type LooseLocalized,
  mergeTranslation,
  readFileBase64,
  type TextDraft,
  toLocalized,
} from "../../lib/messaging";
import styles from "../pages.module.css";
import m from "./messaging.module.css";

// ---------------------------------------------------------------- switch

export function Switch({ checked, onChange, label, disabled, testId }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean; testId?: string }) {
  return (
    <button type="button" role="switch" className={m.switch} aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} data-testid={testId}>
      <span className={m.switchKnob} />
    </button>
  );
}

/** A switch with its label and a line of explanation. */
export function SwitchRow({ title, hint, ...rest }: { title: string; hint?: ReactNode } & Parameters<typeof Switch>[0]) {
  return (
    <div className={m.switchRow}>
      <Switch {...rest} />
      <div className={m.switchText}>
        <strong>{title}</strong>
        {hint ? <p>{hint}</p> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- localized content

export interface ContentState<F extends string = ContentField> {
  draft: TextDraft<F>;
  /** The English text the last translation was made from (englishKey), to warn when it changed since. */
  translatedFrom: string | null;
  /** The last translation's model and cost. */
  translation: { model: string; costUsd: number | null } | null;
}

export const emptyContentState = (): ContentState => ({ draft: emptyDraft(), translatedFrom: null, translation: null });
export const contentStateFrom = (content: Content): ContentState => ({ draft: draftFromContent(content), translatedFrom: null, translation: null });
/** Any localized fields (stories, brain cards). */
export const textState = <F extends string>(fields: readonly F[], content?: LooseLocalized<F>): ContentState<F> => ({
  draft: content ? draftFrom(content, fields) : blankDraft(fields),
  translatedFrom: null,
  translation: null,
});

/** One text field of a localized editor: its label, the API's limit, and a textarea when `rows` is set. */
export interface FieldSpec<F extends string> {
  key: F;
  label: string;
  limit: number;
  rows?: number;
  placeholder?: string;
  hint?: string;
}

/** Announcement and banner fields (contracts/messaging.md "Localized content"). */
export const MESSAGE_FIELD_SPECS: Record<ContentField, FieldSpec<ContentField>> = {
  title: { key: "title", label: "Title", limit: CONTENT_LIMITS.title },
  body: { key: "body", label: "Message", limit: CONTENT_LIMITS.body, rows: 3 },
  detail: { key: "detail", label: "Detail", limit: CONTENT_LIMITS.detail, rows: 6 },
  cta: { key: "cta", label: "Button label", limit: CONTENT_LIMITS.cta },
};

const STATE_LABEL: Record<LocaleState, string> = {
  empty: "not translated: falls back to English",
  partial: "partly translated: the rest falls back to English",
  complete: "translated",
  incomplete: "needs a title and a message",
};

export const fmtUsd = (n: number) => (n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`);

/** The language chips: English, then the 12 others with a dot for their state. */
export function LocaleTabs({ locale, onLocale, stateOf, label = "Languages", testPrefix = "locale" }: { locale: Locale; onLocale: (l: Locale) => void; stateOf: (l: Locale) => LocaleState; label?: string; testPrefix?: string }) {
  return (
    <div className={m.locales} role="tablist" aria-label={label}>
      {LOCALES.map((l) => {
        const state = stateOf(l);
        return (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={l === locale}
            className={m.locale}
            data-state={state}
            title={`${LOCALE_LABEL[l]}: ${l === "en" ? "the source text" : STATE_LABEL[state]}`}
            onClick={() => onLocale(l)}
            data-testid={`${testPrefix}-${l}`}
          >
            {l === "en" ? "EN" : l}
            <span className={m.localeDot} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

/** What a translation call can go wrong with: 503 ai_unavailable (no Anthropic key) gets its own notice. */
export function TranslateError({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError && error.code === "ai_unavailable") {
    return (
      <div className={m.notice} data-tone="warning" role="alert" data-testid="ai-unavailable">
        <strong>{error.message}</strong>
        <span>You can still write each language by hand: pick it above.</span>
      </div>
    );
  }
  return <InlineError error={error} />;
}

export function StaleNotice({ testId = "stale" }: { testId?: string }) {
  return (
    <div className={m.notice} data-tone="warning" data-testid={testId}>
      <strong>English changed since the translation.</strong>
      <span>The other languages may be out of date: translate again, or check them by hand.</span>
    </div>
  );
}

/**
 * English first, then any of the other 12 languages: chips pick the language to edit, "Translate to all
 * languages" fills them with Claude (POST /translate, editor+, with each field's limit), and every translation
 * stays editable. `required`: fields every sent language must have (messaging); without it a language may be
 * partial and falls back to English field by field (learn content).
 */
export function ContentEditor<F extends string>({
  value,
  onChange,
  fields,
  locale,
  onLocale,
  required = [],
  englishRequired,
  needs = {},
  note,
}: {
  value: ContentState<F>;
  onChange: Dispatch<SetStateAction<ContentState<F>>>;
  fields: readonly FieldSpec<F>[];
  locale: Locale;
  onLocale: (l: Locale) => void;
  required?: readonly F[];
  /** English fields needed before translating (default: `required`, else any text). */
  englishRequired?: readonly F[];
  /** Fields the English text needs right now, with why (e.g. the button label once a button is set). */
  needs?: Partial<Record<F, string | null>>;
  /** Shown above the fields, e.g. the claims rule. */
  note?: ReactNode;
}) {
  const canTranslate = useCan("content.translate");
  const translate = useTranslate();
  const keys = fields.map((f) => f.key);
  const { draft } = value;
  const en = draft.en;
  const entry = draft[locale];
  const mustHave = englishRequired ?? required;
  const englishReady = mustHave.length ? mustHave.every((f) => en[f].trim() !== "") : keys.some((f) => en[f].trim() !== "");
  const stateOf = (l: Locale) => localeState(draft[l], keys, { required, en: draft.en });
  const translated = TRANSLATE_TARGETS.filter((l) => ["complete", "partial"].includes(stateOf(l))).length;
  const { incomplete } = toLocalized(draft, keys, required);
  const stale = value.translatedFrom !== null && value.translatedFrom !== englishKey(draft, keys);
  const rtl = RTL_LOCALES.has(locale);

  const setField = (f: F, v: string) => onChange((s) => ({ ...s, draft: { ...s.draft, [locale]: { ...s.draft[locale], [f]: v } } }));

  const runTranslate = () => {
    const sentFrom = englishKey(draft, keys);
    const { content } = toLocalized(draft, keys);
    translate.mutate(
      { content: { en: content.en as Record<string, string> }, limits: Object.fromEntries(fields.map((f) => [f.key, f.limit])) },
      {
        onSuccess: (r) =>
          onChange((s) => ({
            draft: mergeTranslation(s.draft, r.content),
            translatedFrom: sentFrom,
            translation: { model: r.model, costUsd: r.usage?.costUsd ?? null },
          })),
      },
    );
  };

  return (
    <div className={m.content} data-testid="content-editor">
      <div className={m.localeBar}>
        <LocaleTabs locale={locale} onLocale={onLocale} stateOf={stateOf} />
        <div className={m.translateRow}>
          {canTranslate ? (
            <Button
              size="sm"
              variant="secondary"
              busy={translate.isPending}
              disabled={!englishReady}
              onClick={runTranslate}
              data-testid="translate"
              title={englishReady ? undefined : `Write the English ${mustHave.map((f) => fields.find((x) => x.key === f)?.label.toLowerCase()).join(" and ") || "text"} first`}
            >
              {value.translation ? "Translate again" : "Translate to all languages"}
            </Button>
          ) : null}
          <span className={m.translateMeta} data-testid="translate-meta">
            {translated} of {TRANSLATE_TARGETS.length} translated
            {value.translation ? (
              <>
                {" "}
                · {value.translation.model}
                {value.translation.costUsd !== null ? ` · ${fmtUsd(value.translation.costUsd)}` : ""}
              </>
            ) : null}
          </span>
        </div>
      </div>

      <TranslateError error={translate.error} />
      {stale ? <StaleNotice /> : null}
      {incomplete.length ? (
        <div className={m.notice} data-tone="danger" data-testid="incomplete">
          <strong>
            {incomplete.map((l) => LOCALE_LABEL[l]).join(", ")}: needs {required.map((f) => `a ${fields.find((x) => x.key === f)?.label.toLowerCase()}`).join(" and ")}.
          </strong>
          <span>Finish it or clear it; a language without text falls back to English.</span>
        </div>
      ) : null}
      {note}

      <div className={m.localeHead}>
        <span className="monoLabel">{LOCALE_LABEL[locale]}</span>
        {locale !== "en" ? (
          <>
            <span className={styles.faint}>Empty fields fall back to English (shown greyed).</span>
            {stateOf(locale) !== "empty" ? (
              <Button size="sm" variant="ghost" onClick={() => onChange((s) => ({ ...s, draft: { ...s.draft, [locale]: blankDraft(keys).en } }))}>
                Clear {LOCALE_LABEL[locale]}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      {fields.map((f) => {
        const v = entry[f.key];
        const need = locale === "en" && !v.trim() ? needs[f.key] : null;
        const common = {
          value: v,
          maxLength: f.limit,
          dir: rtl ? ("rtl" as const) : undefined,
          lang: locale,
          placeholder: locale === "en" ? f.placeholder : en[f.key] || f.placeholder,
          onChange: (e: { target: { value: string } }) => setField(f.key, e.target.value),
          "data-testid": `field-${f.key}`,
        };
        const hint = (
          <>
            {v.length}/{f.limit}
            {need ? <span className={m.required}> · {need}</span> : null}
            {f.hint ? ` · ${f.hint}` : null}
          </>
        );
        return (
          <Field key={f.key} label={f.label} hint={hint}>
            {f.rows ? <textarea className={styles.textarea} rows={f.rows} {...common} /> : <TextInput {...common} />}
          </Field>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- image

/** Pick or drop a PNG, JPEG or WebP (≤ 1.4 MB): it uploads at once (POST /media) and shows as a thumbnail. */
export function ImageField({
  image,
  onChange,
  recommended,
  hint,
  square,
  shape = square ? "square" : "wide",
  label = "Image · optional",
  testId = "image",
}: {
  image: ImageRef | null;
  onChange: (image: ImageRef | null) => void;
  recommended: { width: number; height: number };
  hint: string;
  /** Show the thumbnail square (compact banners). */
  square?: boolean;
  /** The thumbnail's shape: wide 16:9, square, or portrait 4:5 (story slides). */
  shape?: "wide" | "square" | "portrait";
  label?: string;
  /** Prefix of the test ids (`<testId>-input`, `-thumb`, `-remove`, `-drop`, `-problem`), for pages with several. */
  testId?: string;
}) {
  const canUpload = useCan("media.upload");
  const upload = useUploadMedia();
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const inputId = useId();

  const take = async (file: File | undefined) => {
    if (!file) return;
    upload.reset();
    const p = imageFileProblem(file);
    setProblem(p);
    if (p) return;
    try {
      const data = await readFileBase64(file);
      upload.mutate({ mime: file.type as MediaMime, data }, { onSuccess: (media) => onChange({ id: media.id, url: media.url, width: media.width, height: media.height }) });
    } catch {
      setProblem("Couldn't read that file.");
    }
  };

  const note = image ? aspectNote(image.width, image.height, recommended) : null;
  return (
    <div className={m.image}>
      <span className="monoLabel">{label}</span>
      {image ? (
        <div className={m.imageRow}>
          <img src={image.url} alt="Uploaded" className={cx(m.thumb, shape === "square" && m.thumbSquare, shape === "portrait" && m.thumbPortrait)} data-testid={`${testId}-thumb`} />
          <div className={m.imageInfo}>
            <span className="mono">
              {image.width}×{image.height}
            </span>
            {note ? <span className={m.warnText}>{note}</span> : null}
            {canUpload ? (
              <div className={m.imageActions}>
                <Button size="sm" variant="ghost" onClick={() => input.current?.click()} busy={upload.isPending}>
                  Replace
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onChange(null)} data-testid={`${testId}-remove`}>
                  Remove
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      ) : canUpload ? (
        <div
          className={m.drop}
          data-over={over || undefined}
          role="button"
          tabIndex={0}
          aria-describedby={inputId}
          onClick={() => input.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              input.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void take(e.dataTransfer.files[0]);
          }}
          data-testid={`${testId}-drop`}
        >
          {upload.isPending ? <span>Uploading…</span> : <span>Drop an image here, or click to choose</span>}
          <span className={styles.faint} id={inputId}>
            PNG, JPEG or WebP, up to 1.4 MB · {hint}
          </span>
        </div>
      ) : (
        <span className={styles.faint}>No image. Uploading needs the editor role.</span>
      )}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="visually-hidden"
        tabIndex={-1}
        onChange={(e) => {
          void take(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid={`${testId}-input`}
      />
      {problem ? (
        <div className={m.notice} data-tone="danger" role="alert" data-testid={`${testId}-problem`}>
          <span>{problem}</span>
        </div>
      ) : null}
      <InlineError error={upload.error} />
    </div>
  );
}

// ---------------------------------------------------------------- action

export interface ActionValue {
  action: MessageAction;
  link: AppLink;
  url: string;
  announcementId: string | null;
}
export const noAction = (): ActionValue => ({ action: "none", link: "home", url: "", announcementId: null });

/** Why the action can't be sent yet, or null. The button label is checked by the content editor. */
export function actionProblem(a: ActionValue): string | null {
  if (a.action === "url" && !isHttpsUrl(a.url)) return "Enter a web address starting with https://";
  if (a.action === "announcement" && !a.announcementId) return "Pick the announcement to open";
  return null;
}

/** The API fields for an action (`link` is sent by the caller: it doubles as the push tap target). */
export function actionFields(a: ActionValue): { action: MessageAction; link: AppLink | null; url: string | null; announcementId: string | null } {
  return {
    action: a.action,
    link: a.action === "link" ? a.link : null,
    url: a.action === "url" ? a.url.trim() : null,
    announcementId: a.action === "announcement" ? a.announcementId : null,
  };
}

const ACTION_HINT: Record<MessageAction, string> = {
  none: "No button: the card or message just informs.",
  link: "Opens a place in the app.",
  announcement: "Opens that announcement's detail screen.",
  news_optin: "Turns on News & events, asking for notification permission when needed. A banner with it disappears once the player has turned it on.",
  url: "Opens the page in Safari.",
};

export function ActionFields({
  value,
  onChange,
  actions,
  announcements,
  label = "Button",
}: {
  value: ActionValue;
  onChange: (v: ActionValue) => void;
  actions: readonly MessageAction[];
  /** For the `announcement` action: recent announcements to pick from. */
  announcements?: { id: string; title: string; createdAt: string }[];
  label?: string;
}) {
  const [touched, setTouched] = useState(false);
  const urlBad = value.action === "url" && touched && !isHttpsUrl(value.url);
  return (
    <div className={m.action}>
      <Field label={label} hint={ACTION_HINT[value.action]}>
        <Select value={value.action} onChange={(e) => onChange({ ...value, action: e.target.value as MessageAction })} className={m.select} data-testid="action">
          {actions.map((a) => (
            <option key={a} value={a}>
              {ACTION_LABEL[a]}
            </option>
          ))}
        </Select>
      </Field>
      {value.action === "link" ? (
        <Field label="Opens" hint={NEW_IN_110.has(value.link) ? "New in app 1.1.0: older versions open Home." : undefined}>
          <Select value={value.link} onChange={(e) => onChange({ ...value, link: e.target.value as AppLink })} className={m.select} data-testid="action-link">
            {APP_LINKS.map((l) => (
              <option key={l} value={l}>
                {APP_LINK_LABEL[l]}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      {value.action === "url" ? (
        <Field label="Web address" hint={urlBad ? <span className={m.required}>https:// only, e.g. https://iqdaily.app/tournament</span> : "https:// only"}>
          <TextInput value={value.url} onChange={(e) => onChange({ ...value, url: e.target.value })} onBlur={() => setTouched(true)} placeholder="https://iqdaily.app/…" inputMode="url" data-testid="action-url" aria-invalid={urlBad || undefined} />
        </Field>
      ) : null}
      {value.action === "announcement" ? (
        <Field label="Announcement" hint={announcements && announcements.length === 0 ? "No announcements yet: send one from Notifications first." : undefined}>
          <Select value={value.announcementId ?? ""} onChange={(e) => onChange({ ...value, announcementId: e.target.value || null })} className={m.select} data-testid="action-announcement">
            <option value="">Pick one…</option>
            {(announcements ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.title} · {formatDateTime(a.createdAt)}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- player picker

export type Picked = { userId: string; handle: string | null };

export function PlayerPicker({ picked, onPick }: { picked: Picked | null; onPick: (p: Picked | null) => void }) {
  const [q, setQ] = useState("");
  const search = q.trim();
  const players = usePlayers(search.length >= 2 ? search : "");
  if (picked) {
    return (
      <div className={m.picked}>
        <Chip tone="info">{picked.handle ? `@${picked.handle}` : shortId(picked.userId)}</Chip>
        <Button size="sm" variant="ghost" onClick={() => onPick(null)}>
          Change
        </Button>
      </div>
    );
  }
  return (
    <Field label="Player" hint="Search by @handle, invite code or user id">
      <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="@handle" data-testid="player-search" />
      {search.length >= 2 && players.data ? (
        players.data.items.length ? (
          <ul className={m.results}>
            {players.data.items.slice(0, 6).map((p) => (
              <li key={p.userId}>
                <button type="button" onClick={() => onPick({ userId: p.userId, handle: p.handle })}>
                  <span>{p.handle ? `@${p.handle}` : "no handle"}</span>
                  <span className="mono">{shortId(p.userId)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <span className={styles.faint}>No player found</span>
        )
      ) : null}
    </Field>
  );
}
